import { useEffect } from "react"
import { Button, TextField, Typography } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { useForm, Controller } from "react-hook-form"
import { useSelector } from "react-redux"
import dayjs, { Dayjs } from "dayjs"
import "dayjs/locale/ru"
// eslint-disable-next-line import/no-extraneous-dependencies
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
// eslint-disable-next-line import/no-extraneous-dependencies
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
// eslint-disable-next-line import/no-extraneous-dependencies
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { IdentificationByPassportRequest, PassportFormData } from "../../types/identification"
import { InputTitle, Input, DateInput } from "./styled"
import { formatPassport, formatPassportIssuerCode } from "../../utils/formatters"
import { useIdentificationByPassport } from "../../api/hooks/useIdentificationByPassport"
import { QUERY_PARAMS_EXCHANGE_TOKEN_KEY, ROUTES_PATHS } from "../../utils/constants"
import { RootState } from "../../store/store"
import { validateINN } from "../../utils/validators"


const PassportPage = () => {
  const navigate = useNavigate()
  const token = useSelector((state: RootState) => state.auth.token)
  const {
    data: identificationResult,
    mutate: identificationMutate,
    status: identificationStatus,
  } = useIdentificationByPassport()
  const { register, handleSubmit, formState: { errors }, control } = useForm<PassportFormData>()

  useEffect(() => {
    if (identificationStatus === "error") {
      navigate(`/${ROUTES_PATHS.error}`)
      return
    }

    if (identificationResult?.error) {
      navigate({
        pathname: `/${ROUTES_PATHS.topUpMbankAccount}/identification/failed`,
      })
    }

    if (identificationResult?.identificationRequestId) {
      navigate(
        `/${ROUTES_PATHS.topUpMbankAccountIdentification}/${identificationResult.identificationRequestId}?${QUERY_PARAMS_EXCHANGE_TOKEN_KEY}=${token}`,
      )
    }
  }, [identificationResult, identificationStatus, navigate, token])

  const onSubmit = (data: PassportFormData) => {
    const requestData: IdentificationByPassportRequest = {
      firstName: data.firstName,
      lastName: data.lastName,
      patronymic: data.patronymic,
      dateOfBirth: dayjs(data.dateOfBirth, "DD.MM.YYYY").format("YYYY-MM-DD"),
      taxNumber: data.taxNumber,
      citizenship: "RUS",
      identificationDocument: {
        "number": data.passportNumber.replace(/\D/g, ""),
        "documentType": "passport_ru",
        "issuer": data.passportIssuer,
        "issuerCode": data.passportIssuerCode.replace(/\D/g, ""),
        "issueDate": dayjs(data.passportDate, "DD.MM.YYYY").format("YYYY-MM-DD")
      },
    }
    identificationMutate(requestData)
  }

  return (
    <form
      id="passportForm"
      onSubmit={handleSubmit(onSubmit)}
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
      }}
    >
      <Typography variant="h4Bold" sx={InputTitle}>
        Персональные данные
      </Typography>
      <TextField
        fullWidth
        placeholder="Фамилия"
        sx={Input}
        error={!!errors.lastName}
        helperText={errors.lastName?.message}
        {...register("lastName", {
          required: "Заполните поле",
          pattern: {
            value: /^[А-Яа-яЁё]+$/,
            message: "Используйте только русские буквы"
          }
        })}
      />
      <TextField
        fullWidth
        placeholder="Имя"
        sx={Input}
        error={!!errors.firstName}
        helperText={errors.firstName?.message}
        {...register("firstName", {
          required: "Заполните поле",
          pattern: {
            value: /^[А-Яа-яЁё]+$/,
            message: "Используйте только русские буквы"
          }
        })}
      />
      <TextField
        fullWidth
        placeholder="Отчество"
        sx={Input}
        error={!!errors.patronymic}
        helperText={errors.patronymic?.message}
        {...register("patronymic", {
          required: "Заполните поле",
          pattern: {
            value: /^[А-Яа-яЁё]+$/,
            message: "Используйте только русские буквы"
          }
        })}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
        <Controller
          name="dateOfBirth"
          control={control}
          rules={{ required: "Заполните поле" }}
          render={({ field }) => (
            <DatePicker
              value={field.value ? dayjs(field.value, "DD.MM.YYYY") : null}
              onChange={(date: Dayjs | null) => {
                field.onChange(date ? date.format("DD.MM.YYYY") : "")
              }}
              format="DD.MM.YYYY"
              slotProps={{
                textField: {
                  placeholder: "Дата рождения",
                  fullWidth: true,
                  variant: "filled",
                  color: "primary",
                  sx: DateInput(!!errors.dateOfBirth),
                  error: !!errors.dateOfBirth,
                  helperText: errors.dateOfBirth?.message,
                }
              }}
            />
          )}
        />
      </LocalizationProvider>
      <Controller
        name="taxNumber"
        control={control}
        rules={{
          required: "Заполните поле",
          validate: (value) => {
            const digitsOnly = value.replace(/\D/g, "")
            if (digitsOnly.length !== 12) {
              return "ИНН должен содержать 12 цифр"
            }
            if (!validateINN(digitsOnly)) {
              return "Указан некорректный ИНН"
            }
            return true
          }
        }}
        render={({ field }) => (
          <TextField
            fullWidth
            placeholder="ИНН"
            sx={Input}
            error={!!errors.taxNumber}
            helperText={errors.taxNumber?.message}
            value={field.value || ""}
            onChange={(e) => {
              const formatted = e.target.value.replace(/\D/g, "")
              field.onChange(formatted.slice(0, 12))
            }}
            onBlur={field.onBlur}
          />
        )}
      />

      <Typography variant="h4Bold" sx={InputTitle}>
        Паспортные данные
      </Typography>
      <Controller
        name="passportNumber"
        control={control}
        rules={{
          required: "Заполните поле",
          validate: (value) => {
            const digitsOnly = value.replace(/\D/g, "")
            if (digitsOnly.length !== 10) {
              return "Серия и номер паспорта должны содержать 10 цифр"
            }
            return true
          }
        }}
        render={({ field }) => (
          <TextField
            fullWidth
            placeholder="Серия и номер паспорта"
            sx={Input}
            error={!!errors.passportNumber}
            helperText={errors.passportNumber?.message}
            value={field.value || ""}
            onChange={(e) => {
              const formatted = formatPassport(e.target.value)
              field.onChange(formatted)
            }}
            onBlur={field.onBlur}
          />
        )}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
        <Controller
          name="passportDate"
          control={control}
          rules={{ required: "Заполните поле" }}
          render={({ field }) => (
            <DatePicker
              value={field.value ? dayjs(field.value, "DD.MM.YYYY") : null}
              onChange={(date: Dayjs | null) => {
                field.onChange(date ? date.format("DD.MM.YYYY") : "")
              }}
            format="DD.MM.YYYY"
              slotProps={{
                textField: {
                  placeholder: "Дата выдачи",
                  fullWidth: true,
                  variant: "filled",
                  color: "primary",
                  sx: DateInput(!!errors.passportDate),
                  error: !!errors.passportDate,
                  helperText: errors.passportDate?.message,
                }
              }}
            />
          )}
        />
      </LocalizationProvider>
      <TextField
        fullWidth
        placeholder="Кем выдан"
        sx={Input}
        error={!!errors.passportIssuer}
        helperText={errors.passportIssuer?.message}
        inputProps={{ maxLength: 250 }}
        {...register("passportIssuer", {
          required: "Заполните поле",
          pattern: {
            value: /^[А-Яа-яЁё0-9\s.,\-()/№«»:]+$/,
            message: "Используйте только русские буквы, цифры и пробелы"
          }
        })}
      />
      <Controller
        name="passportIssuerCode"
        control={control}
        rules={{
          validate: (value) => {
            if (!value || value.trim() === "") {
              return "Заполните поле"
            }
            const digitsOnly = value.replace(/\D/g, "")
            if (digitsOnly.length !== 6) {
              return "Код подразделения должен содержать 6 цифр"
            }
            return true
          }
        }}
        render={({ field }) => (
          <TextField
            fullWidth
            placeholder="Код поздразделения"
            sx={Input}
            error={!!errors.passportIssuerCode}
            helperText={errors.passportIssuerCode?.message}
            value={field.value || ""}
            onChange={(e) => {
              const formatted = formatPassportIssuerCode(e.target.value)
              field.onChange(formatted)
            }}
            onBlur={field.onBlur}
          />
        )}
      />

      <Button
        variant="contained"
        type="submit"
        sx={{ marginTop: "auto" }}
      >
        Отправить
      </Button>
    </form>
  )
}

export default PassportPage
